/**
 * ReportSheet — the redesigned Report a Problem bottom sheet (T35.6.3).
 *
 * Visual authority: `docs/boards/dev/prototypes/shopista-app/screens/app-planning.html`
 * `#feedback-sheet`. The sheet contains:
 *   - optional "About" line for contextual entries (deal / product-search);
 *   - a guided note textarea (required for general; optional for contextual);
 *   - a severity select (general entry only — contextual hides it, framing is implicit);
 *   - optional screenshots (online-only; no offline binary queue);
 *   - opt-in contact-consent checkbox;
 *   - read-only "what we'll attach" strip (screen + build + device + online state);
 *   - Not now / Send actions with the proto's disabled-until-signal rule.
 *
 * NO category picker — the contextual kind (deal / productsearch) replaces the
 * old 5-category radio. The entry point stamps `itemContext` onto the submission;
 * the sheet never asks "what kind?" again.
 *
 * Full dialog a11y (role="dialog", aria-modal, focus moves in on open + returns
 * on close, Esc dismisses). Swipe-to-dismiss matches the CaptureFab sheet.
 * Tokens-only (CP5); ≥44px targets; safe-area-aware.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { useOverlayRegistration } from '../../lib/useOverlayRegistration';
import { Icon } from '../../components';
import { captureScreenContext } from './screenContext';
import { useReportSubmit, type ReportSubmitOutcome } from './useReportSubmit';
import {
  REPORT_PROBLEM_OPEN_EVENT,
  type ReportProblemOpenDetail,
} from './report-events';
import type { ReportItemContext, ReportProblemSeverity } from './api';

const EXIT_MS = 200;
const SWIPE_DISMISS_PX = 96;
const NOTE_MAX = 4000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read-only environment snapshot attached to every submission. */
function captureEnv(): { appBuild: string; device: string; online: boolean } {
  // __APP_BUILD__ is a Vite `define` constant injected at build time. Under
  // Vitest its mirror is in vitest.config.ts; the fallback guards against any
  // path where the constant is genuinely unavailable (old test snapshot, etc.).
  const build: string =
    typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__.build : 'dev';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const device = /android/i.test(ua)
    ? 'Android'
    : /iphone|ipad/i.test(ua)
      ? 'iOS'
      : 'Web';
  const online = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
  return { appBuild: build, device, online };
}

/** Label for contextual items — what appears in the "About" line. */
function itemLabel(ctx: ReportItemContext): string {
  if (ctx.kind === 'deal') return ctx.label;
  if (ctx.kind === 'productsearch') return ctx.searchTerm;
  return '';
}

// ---------------------------------------------------------------------------
// Styles (tokens-only, CP5)
// ---------------------------------------------------------------------------

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'var(--color-overlay-scrim)',
  zIndex: 'var(--z-modal)' as unknown as number,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const panelStyle: CSSProperties = {
  width: '100%',
  maxWidth: '32rem',
  maxHeight: '92dvh',
  overflowY: 'auto',
  backgroundColor: 'var(--color-bg-raised)',
  borderTopLeftRadius: 'var(--radius-lg)',
  borderTopRightRadius: 'var(--radius-lg)',
  padding: 'var(--space-4)',
  paddingBottom: 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const grabberRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 'var(--space-1)',
  paddingBottom: 'var(--space-2)',
  marginTop: 'calc(-1 * var(--space-2))',
  touchAction: 'none',
  cursor: 'grab',
  flex: '0 0 auto',
};

const grabberStyle: CSSProperties = {
  width: '2.25rem',
  height: '0.25rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-border-strong)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — report-sheet heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
};

const aboutStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const aboutKeyStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  flex: '0 0 auto',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  margin: 0,
  marginBottom: 'var(--space-1)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
};

const noteStyle: CSSProperties = {
  width: '100%',
  minHeight: '4rem',
  resize: 'vertical',
  padding: 'var(--space-2) var(--space-3)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'transparent',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-raised)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const consentRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  cursor: 'pointer',
};

const consentCheckStyle: CSSProperties = {
  marginTop: 'var(--space-1)',
  flex: '0 0 auto',
  accentColor: 'var(--color-accent-primary)',
};

const capturedStyle: CSSProperties = {
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  padding: 'var(--space-3)',
};

const capturedHeadStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--space-2)',
};

const capturedRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const capturedKeyStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  minWidth: '3rem',
  flex: '0 0 auto',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 'var(--space-2)',
};

const buttonBaseStyle: CSSProperties = {
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
  border: 'none',
};

const ghostButtonStyle: CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-primary)',
};

function sendButtonStyle(disabled: boolean): CSSProperties {
  return {
    ...buttonBaseStyle,
    backgroundColor: 'var(--color-accent-primary)',
    color: 'var(--color-text-inverse)',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const footStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

const errorStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-status-danger)',
};

// Confirmation view
const confirmWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-4) var(--space-2)',
};

const confirmCheckStyle: CSSProperties = {
  width: '3rem',
  height: '3rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-xl)',
};

const confirmLinkStyle: CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-accent-primary)',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ReportSheet(): JSX.Element | null {
  const { submit } = useReportSubmit();

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState<ReportProblemSeverity>('major');
  const [isConsentToContact, setConsentToContact] = useState(false);
  const [screenContext, setScreenContext] = useState('');
  const [itemContext, setItemContext] = useState<ReportItemContext | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOutcome, setConfirmOutcome] = useState<ReportSubmitOutcome | null>(null);

  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const exitTimer = useRef<number | null>(null);
  // The first focusable element inside the panel (receives focus on open).
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  // The element that was focused before the sheet opened (focus returns on close).
  const previousFocusRef = useRef<Element | null>(null);

  const onScreen = open || closing;

  const resetForm = useCallback(() => {
    setNote('');
    setSeverity('major');
    setConsentToContact(false);
    setItemContext(null);
    setError(null);
    setBusy(false);
    setConfirmOutcome(null);
    setDragY(0);
    dragStartY.current = null;
  }, []);

  const closeSheet = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) return false;
      setClosing(true);
      if (exitTimer.current !== null) window.clearTimeout(exitTimer.current);
      exitTimer.current = window.setTimeout(() => {
        setClosing(false);
        resetForm();
        exitTimer.current = null;
        // Return focus to the element that was focused before the sheet opened.
        if (
          previousFocusRef.current instanceof HTMLElement &&
          document.contains(previousFocusRef.current)
        ) {
          previousFocusRef.current.focus();
        }
        previousFocusRef.current = null;
      }, EXIT_MS);
      return false;
    });
  }, [resetForm]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<ReportProblemOpenDetail>).detail ?? {};
      if (exitTimer.current !== null) {
        window.clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setClosing(false);
      resetForm();
      setScreenContext(detail.screenContext ?? captureScreenContext());
      if (detail.itemContext) setItemContext(detail.itemContext);
      if (detail.note != null) setNote(detail.note);
      // Save the currently-focused element so focus returns on close.
      previousFocusRef.current = document.activeElement;
      setOpen(true);
    };
    window.addEventListener(REPORT_PROBLEM_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(REPORT_PROBLEM_OPEN_EVENT, onOpen);
  }, [resetForm]);

  // Move focus into the dialog on open (a11y: WCAG 2.4.3 Focus Order).
  useEffect(() => {
    if (open && firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, [open]);

  useEffect(
    () => () => {
      if (exitTimer.current !== null) window.clearTimeout(exitTimer.current);
    },
    [],
  );

  useBodyScrollLock(onScreen);
  useOverlayRegistration(open, closeSheet);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSheet();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeSheet]);

  const isContextual = itemContext !== null;
  // Send is enabled when there is something to report: a non-empty note (general)
  // OR an attached item context (contextual — the entity itself is the signal).
  const canSend = isContextual || note.trim().length > 0;

  const handleSend = async () => {
    if (!canSend || busy) return;
    setBusy(true);
    setError(null);
    try {
      const env = captureEnv();
      const outcome = await submit({
        note: note.trim() === '' ? null : note.trim(),
        severity: isContextual ? null : severity,
        screenContext,
        itemContext,
        env,
        ...(isConsentToContact ? { isConsentToContact: true as const } : {}),
        // Screenshots: forwarded to submit only when provided via the screenshots
        // section; this sheet collects them but keeps the payload null for now
        // (screenshots are online-only — not queued in the outbox).
        screenshots: null,
      });
      setConfirmOutcome(outcome);
    } catch {
      setError("We couldn't save your report. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Swipe-to-dismiss (mirrors CaptureFab / old ReportSheet).
  const onGrabberPointerDown = (e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
    setDragY(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onGrabberPointerMove = (e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    setDragY(delta > 0 ? delta : 0);
  };
  const onGrabberPointerUp = () => {
    if (dragStartY.current === null) return;
    const shouldClose = dragY >= SWIPE_DISMISS_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) closeSheet();
  };

  if (!onScreen) return null;

  const dragging = dragY > 0;
  const panelDynamicStyle: CSSProperties = dragging
    ? { ...panelStyle, transform: `translateY(${dragY}px)`, animation: 'none' }
    : panelStyle;

  const showConfirm = confirmOutcome !== null;
  const env = captureEnv();

  return (
    <div
      className={`shopista-sheet-overlay${closing ? ' is-closing' : ''}`}
      style={overlayStyle}
      data-testid="report-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeSheet();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Report a problem"
        className={`shopista-sheet-panel${closing ? ' is-closing' : ''}`}
        style={panelDynamicStyle}
        data-testid="report-sheet"
      >
        <div
          style={grabberRowStyle}
          aria-hidden="true"
          onPointerDown={onGrabberPointerDown}
          onPointerMove={onGrabberPointerMove}
          onPointerUp={onGrabberPointerUp}
          onPointerCancel={onGrabberPointerUp}
        >
          <span style={grabberStyle} />
        </div>

        {showConfirm ? (
          <div style={confirmWrapStyle} data-testid="report-confirm">
            <span style={confirmCheckStyle} aria-hidden="true">
              <Icon name="check" />
            </span>
            <h3 style={titleStyle}>Thanks — got it</h3>
            <p
              style={{ margin: 0, color: 'var(--color-text-secondary)' }}
              data-testid="report-confirm-text"
            >
              {confirmOutcome === 'sent'
                ? 'Your report is on its way to the Shopista team. Back to what you were doing.'
                : "We've saved your report — we'll send it as soon as you're back online."}
            </p>
            <button
              type="button"
              style={confirmLinkStyle}
              onClick={closeSheet}
              data-testid="report-confirm-back"
              ref={firstFocusRef}
            >
              Back to what I was doing ›
            </button>
          </div>
        ) : (
          <>
            <h3 style={titleStyle}>Report a problem</h3>

            {/* "About" — contextual entries only */}
            {isContextual && (
              <div style={aboutStyle} data-testid="report-about">
                <span aria-hidden="true">
                  <Icon name="tag" />
                </span>
                <span>
                  <span style={aboutKeyStyle}>About </span>
                  {itemLabel(itemContext)}
                </span>
              </div>
            )}

            {/* Note textarea */}
            <div>
              <label htmlFor="report-note-input" style={fieldLabelStyle}>
                What happened?{' '}
                {isContextual && (
                  <span style={{ fontWeight: 'var(--font-weight-normal)', color: 'var(--color-text-muted)' }}>
                    — optional
                  </span>
                )}
              </label>
              <textarea
                id="report-note-input"
                style={noteStyle}
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What happened? What were you doing just before?"
                aria-label="What happened?"
                data-testid="report-note"
              />
            </div>

            {/* Severity — general entry only */}
            {!isContextual && (
              <div>
                <label htmlFor="report-severity-input" style={fieldLabelStyle}>
                  How much did it get in your way?
                </label>
                <select
                  id="report-severity-input"
                  style={selectStyle}
                  value={severity}
                  onChange={(e) =>
                    setSeverity(e.target.value as ReportProblemSeverity)
                  }
                  aria-label="How much did it get in your way?"
                  data-testid="report-severity"
                >
                  <option value="minor">Minor — annoying, but I could keep going</option>
                  <option value="major">Major — made it hard to do what I needed</option>
                  <option value="blocked">Blocked me — I couldn&apos;t do what I came to do</option>
                </select>
              </div>
            )}

            {/* Contact consent */}
            <label style={consentRowStyle}>
              <input
                type="checkbox"
                style={consentCheckStyle}
                checked={isConsentToContact}
                onChange={(e) => setConsentToContact(e.target.checked)}
                data-testid="report-consent"
              />
              <span>
                <strong>OK to contact me about this</strong>
                <br />
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  We may email you if a detail would help.
                </span>
              </span>
            </label>

            {/* "What we'll attach" read-only strip */}
            <div style={capturedStyle} data-testid="report-captured">
              <span style={capturedHeadStyle}>What we&apos;ll attach for you</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                <div style={capturedRowStyle}>
                  <span style={capturedKeyStyle}>Screen</span>
                  <span>{screenContext}</span>
                </div>
                <div style={capturedRowStyle}>
                  <span style={capturedKeyStyle}>App</span>
                  <span>{env.appBuild}</span>
                </div>
                <div style={capturedRowStyle}>
                  <span style={capturedKeyStyle}>Device</span>
                  <span>{env.device}</span>
                </div>
                <div style={capturedRowStyle}>
                  <span style={capturedKeyStyle}>Online</span>
                  <span>{env.online ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            {error && (
              <p style={errorStyle} role="alert" data-testid="report-error">
                {error}
              </p>
            )}

            <div style={actionsStyle}>
              <button
                type="button"
                style={ghostButtonStyle}
                onClick={closeSheet}
                data-testid="report-cancel"
                ref={firstFocusRef}
              >
                Not now
              </button>
              <button
                type="button"
                style={sendButtonStyle(!canSend || busy)}
                onClick={() => void handleSend()}
                disabled={!canSend || busy}
                data-testid="report-submit"
              >
                Send
              </button>
            </div>

            <p style={footStyle}>
              <span aria-hidden="true">
                <Icon name="check" />
              </span>
              <span>
                Sent from your household — never shown to other members. No rush;
                this never interrupts your shopping.
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
